import { getFirestore, doc, getDoc } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { APP_VERSION } from "../../version.js";
import { ModalManager } from "../../utils/ModalManager.js";
import { InfoModal } from "../modals/InfoModal.js";
import { ReportModal } from "../report-modal.js";
import { MarkdownModal } from "../markdown-modal.js";
import { locale } from "../../services/LocaleService.js";

const db = getFirestore(app);

export class Footer {
    constructor() {
        this.element = document.createElement('footer');
        this.element.className = "w-full py-10 px-4 mt-auto bg-white dark:bg-zinc-900 border-t border-zinc-100 dark:border-zinc-800 text-center transition-colors duration-300 z-0 relative";
    }

    async render() {
        this.element.innerHTML = `
            <div class="max-w-7xl mx-auto flex flex-col gap-6 items-center">
                <!-- Layer 1: Legal Links -->
                <div class="flex flex-wrap justify-center gap-4 text-xs tracking-wide text-zinc-400 dark:text-zinc-500">
                    <button id="footer-terms" class="hover:text-zinc-800 dark:hover:text-zinc-200 transition-colors">${locale.t('footer.terms')}</button>
                    <span class="text-zinc-200 dark:text-zinc-700">|</span>
                    <button id="footer-privacy" class="font-bold text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">${locale.t('footer.privacy')}</button>
                    <span class="text-zinc-200 dark:text-zinc-700">|</span>
                    <button id="footer-report" class="hover:text-zinc-800 dark:hover:text-zinc-200 transition-colors">${locale.t('footer.report')}</button>
                </div>

                <!-- Layer 2: Business Info -->
                <div id="footer-biz-info" class="flex flex-wrap justify-center gap-x-3 gap-y-1 text-[10px] text-zinc-400 dark:text-zinc-400 leading-relaxed max-w-3xl">
                    <!-- Dynamic Content -->
                    <span class="animate-pulse bg-zinc-100 dark:bg-zinc-800 h-3 w-24 rounded"></span>
                </div>

                <!-- Layer 3: System -->
                <div class="text-[10px] font-mono text-zinc-300 dark:text-zinc-500 uppercase tracking-widest opacity-50">
                    MIRINAE_OS_${APP_VERSION} // ${locale.t('footer.system_ready')}
                </div>
            </div>
        `;

        this.bindEvents();

        // Lazy load business info
        this.loadBusinessInfo();

        return this.element;
    }

    bindEvents() {
        const termsBtn = this.element.querySelector('#footer-terms');
        const privacyBtn = this.element.querySelector('#footer-privacy');
        const reportBtn = this.element.querySelector('#footer-report');

        if (termsBtn) termsBtn.onclick = () => ModalManager.show(InfoModal, 'terms', locale.t('footer.terms'));
        if (privacyBtn) privacyBtn.onclick = () => ModalManager.show(InfoModal, 'privacy', locale.t('footer.privacy'));

        // [FIX] Report Center Link Modification (Final)
        // User requested the "Drawer Link Popup" style (Dark Glassmorphism, Image 2).
        // This confirms they want 'InfoModal' (Cosmic Style) displaying the Document 'doc_mkf4py9w'.
        if (reportBtn) {
            reportBtn.onclick = () => ModalManager.show(InfoModal, 'doc_mkf4py9w', locale.t('footer.report'));
        }
    }

    async loadBusinessInfo() {
        try {
            const docRef = doc(db, "settings", "businessInfo");
            const snap = await getDoc(docRef);
            const container = this.element.querySelector('#footer-biz-info');

            if (snap.exists()) {
                const data = snap.data();

                // [FIX] Localization Support for Business Info
                // If translations exist for current language, merge them over base data
                let displayData = data;
                const currentLang = localStorage.getItem('mirinae_lang') || 'ko';

                if (currentLang !== 'ko' && data.translations?.[currentLang]) {
                    displayData = { ...data, ...data.translations[currentLang] };
                }

                const fields = [
                    { label: '', value: displayData.companyName, bold: true },
                    { label: locale.t('footer.biz_ceo'), value: displayData.ceo },
                    { label: locale.t('footer.biz_license'), value: displayData.licenseNum },
                    { label: locale.t('footer.biz_mail_order'), value: displayData.mailOrderLicense },
                    { label: locale.t('footer.biz_addr'), value: displayData.addr },
                    { label: locale.t('footer.biz_tel'), value: displayData.tel },
                    { label: locale.t('footer.biz_email'), value: displayData.email },
                ];

                const html = fields
                    .filter(f => f.value && f.value.trim() !== '')
                    .map(f => {
                        const text = f.label ? `${f.label}: ${f.value}` : f.value;
                        const className = f.bold
                            ? "font-bold text-zinc-500 dark:text-zinc-400"
                            : "";
                        return `<span class="${className}">${text}</span>`;
                    })
                    .join('<span class="mx-1 text-zinc-200 dark:text-zinc-800">|</span>');

                container.innerHTML = html || `<span>(${locale.t('footer.biz_none')})</span>`;
            } else {
                container.innerHTML = `<span>${locale.t('footer.copyright')} © 2026</span>`;
            }
        } catch (e) {
            console.warn("[Footer] Failed to load business info:", e);
            // Fallback
            const container = this.element.querySelector('#footer-biz-info');
            if (container) container.innerHTML = `<span>${locale.t('footer.copyright')}</span>`;
        }
    }
}
